"use client";

import type * as React from "react";

// TODO: migrate to react aria alternative
import * as SelectPrimitive from "@radix-ui/react-select";
import ChevronDown16 from "@ui/icons/16x16/chevron-down-16.svg";
import { cn } from "@ui/lib/utils";
import { type VariantProps, cva } from "class-variance-authority";

export type SelectTriggerProps = Omit<VariantProps<typeof selectVariants>, "icon"> & { icon?: React.ReactNode };

const selectVariants = cva(
  "inline-flex items-center justify-between whitespace-nowrap rounded-full effect-color disabled:cursor-not-allowed",
  {
    variants: {
      variant: {
        primary: "bg-inverse text-inverse effect-focus-light data-[state=active]:bg-darkest",
        secondary: "bg-dark text-default effect-focus-light data-[state=active]:bg-overlay-darker",
        tertiary:
          "bg-default text-default effect-focus-inverse-light ring-offset-darkest data-[state=active]:bg-overlay-lighter",
        quaternary: "text-inverse effect-focus-inverse-light ring-offset-darkest data-[state=active]:bg-overlay-light",
        quinary:
          "text-inverse effect-focus-inverse-light bg-overlay-light ring-offset-darkest data-[state=active]:bg-overlay-lighter",
        outline:
          "bg-default border text-default effect-focus-inverse ring-offset-darkest data-[state=active]:bg-overlay-lighter",
        "outline-new":
          "border text-default effect-focus-inverse ring-offset-darkest data-[state=active]:bg-overlay-lighter",
        "outline-inverse":
          "bg-transparent border border-overlay-light text-inverse effect-focus-inverse-light data-[state=active]:bg-default data-[state=active]:text-default data-[state=active]:border-inverse",
        ghost: "bg-transparent text-default hover:text-lighter effect-focus data-[state=active]:bg-default",
        "ghost-inverse": "bg-transparent text-inverse hover:text-lighter effect-focus data-[state=active]:bg-default",
        elevated:
          "bg-default rounded-full shadow-[0_0_0_1px_rgba(0,0,0,0.06),0_1px_2px_0_rgba(0,0,0,0.04),0_2px_4px_0_rgba(0,0,0,0.04)] hover:bg-dark",
      },
      expand: {
        true: "w-full",
        false: "w-fit",
      },
      size: {
        sm: "h-28 f-paragraph-03 ps-12 pe-8",
        md: "h-40 f-paragraph-02 ps-16 pe-12",
        lg: "h-48 f-paragraph-02 ps-20 pe-16",
      },
      icon: {
        true: "",
        false: "",
      },
      overlayStyle: {
        true: "",
        false: "",
      },
    },
    compoundVariants: [
      // Overlay style border
      {
        overlayStyle: true,
        variant: "outline-new",
        className: "tw-border-black/[0.08] transition-colors",
      },
      // Overlay style hover
      {
        overlayStyle: true,
        variant: "outline-new",
        className: "enabled:hover:bg-black/[0.05]",
      },
      {
        overlayStyle: true,
        variant: "outline-new",
        className: "hover:bg-black/[0.05]",
      },
      // Icons
      {
        icon: true,
        size: ["md"],
        className: "ps-10",
      },
      {
        icon: true,
        size: ["lg"],
        className: "ps-12",
      },
      // Hover
      {
        variant: "primary",
        className: "enabled:hover:bg-darkest",
      },
      {
        variant: "primary",
        className: "hover:bg-darkest",
      },
      {
        variant: "secondary",
        className: "enabled:hover:bg-overlay-darker",
      },
      {
        variant: "secondary",
        className: "hover:bg-overlay-darker",
      },
      {
        variant: "tertiary",
        className: "enabled:hover:bg-overlay-lighter",
      },
      {
        variant: "tertiary",
        className: "hover:bg-overlay-lighter",
      },
      {
        variant: "quaternary",
        className: "enabled:hover:bg-overlay-light",
      },
      {
        variant: "quaternary",
        className: "hover:bg-overlay-light",
      },
      {
        variant: "quinary",
        className: "enabled:hover:bg-overlay-lighter",
      },
      {
        variant: "quinary",
        className: "hover:bg-overlay-lighter",
      },
      {
        variant: "outline",
        className: "enabled:hover:bg-overlay-lighter enabled:hover:border-darkest",
      },
      {
        variant: "outline",
        className: "hover:bg-overlay-lighter hover:border-darkest",
      },
      {
        variant: "outline-inverse",
        className: "enabled:hover:bg-default enabled:hover:text-default",
      },
      {
        variant: "outline-inverse",
        className: "hover:bg-default hover:text-default",
      },
    ],
    defaultVariants: {
      variant: "outline",
      size: "md",
      expand: false,
    },
  },
);

const Select = SelectPrimitive.Root;

const SelectValue = SelectPrimitive.Value;

export interface SelectTriggerComponentProps
  extends React.ComponentPropsWithoutRef<typeof SelectPrimitive.Trigger>, SelectTriggerProps {
  ref?: React.Ref<React.ElementRef<typeof SelectPrimitive.Trigger>>;
  showDropdownArrow?: boolean;
}

const SelectTrigger = ({
  className,
  variant,
  size,
  expand,
  icon,
  overlayStyle,
  children,
  ref,
  showDropdownArrow = true,
  ...props
}: SelectTriggerComponentProps) => (
  <SelectPrimitive.Trigger
    className={cn(
      selectVariants({
        variant,
        size,
        className,
        expand,
        icon: !!icon,
        overlayStyle,
      }),
    )}
    ref={ref}
    {...props}
  >
    <span className='flex min-w-0 flex-1 items-center gap-8'>
      {icon && <span className='flex-shrink-0'>{icon}</span>}
      <span className='truncate'>{children}</span>
    </span>
    {showDropdownArrow && (
      <SelectPrimitive.Icon asChild>
        <ChevronDown16 />
      </SelectPrimitive.Icon>
    )}
  </SelectPrimitive.Trigger>
);

SelectTrigger.displayName = SelectPrimitive.Trigger.displayName;

interface SelectContentComponentProps extends React.ComponentPropsWithoutRef<typeof SelectPrimitive.Content> {
  ref?: React.Ref<React.ElementRef<typeof SelectPrimitive.Content>>;
}

const SelectContent = ({ className, children, position = "popper", ref, ...props }: SelectContentComponentProps) => (
  <SelectPrimitive.Portal>
    <SelectPrimitive.Content
      className={cn(
        "relative z-9999 min-w-[var(--radix-select-trigger-width)] rounded-xl border border-default bg-default p-6",
        // Animation values
        "data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2",
        position === "popper" &&
          "data-[side=bottom]:translate-y-4 data-[side=left]:-translate-x-4 data-[side=right]:translate-x-4 data-[side=top]:-translate-y-4",
        className,
      )}
      position={position}
      ref={ref}
      {...props}
    >
      <SelectPrimitive.Viewport
        className={cn(position === "popper" && "h-[var(--radix-select-trigger-height)] w-full")}
      >
        {children}
      </SelectPrimitive.Viewport>
    </SelectPrimitive.Content>
  </SelectPrimitive.Portal>
);

SelectContent.displayName = SelectPrimitive.Content.displayName;

interface SelectItemComponentProps extends React.ComponentPropsWithoutRef<typeof SelectPrimitive.Item> {
  icon?: React.ReactNode;
  ref?: React.Ref<React.ElementRef<typeof SelectPrimitive.Item>>;
  fontVariant?: "default" | "new";
}

const SelectItem = ({
  className,
  children,
  icon,
  ref,
  fontVariant = "default",
  ...props
}: SelectItemComponentProps) => (
  <SelectPrimitive.Item
    className={cn(
      "flex h-40 w-full items-center gap-8 rounded-lg px-16 outline-none data-[highlighted]:bg-dark",
      fontVariant === "default" ? "f-ui-03" : "f-paragraph-02",
      `${icon ? "pl-10" : "ps-16"}`,
      className,
    )}
    ref={ref}
    {...props}
  >
    {icon && icon}
    <SelectPrimitive.ItemText>{children}</SelectPrimitive.ItemText>
  </SelectPrimitive.Item>
);

SelectItem.displayName = SelectPrimitive.Item.displayName;

export { Select, SelectContent, SelectItem, SelectTrigger, SelectValue };
